/**
 * <cart-progress-bar> — smooth fill animation for the cart reward progress bar.
 *
 * Why this exists: the bar is server-rendered and the Dawn cart re-renders by
 * swapping the whole `.drawer__inner` / `#CartDrawer` innerHTML on every cart
 * change. That recreates the fill node with its final width already set, so a
 * plain CSS `transition` never fires and the bar "snaps" (reads as lag).
 *
 * This component runs on every re-render (connectedCallback fires each time the
 * fresh node is inserted). It remembers the previous fill width in a module-level
 * map, paints the new node at that previous width WITHOUT transition, then on the
 * next frame sets the server's target width WITH transition — so the fill slides
 * old → new. The cart core is untouched.
 *
 * Gated by Theme settings → Cart Progress Bar → Animate progress bar fill
 * (data-animate). Only the multi-tier `.shipping-threshold__fill` is animated;
 * the single-tier <progress> is left as-is (its value can't transition reliably).
 */
if (!customElements.get('cart-progress-bar')) {
  const lastWidth = new Map();

  customElements.define(
    'cart-progress-bar',
    class CartProgressBar extends HTMLElement {
      connectedCallback() {
        if (this.dataset.animate !== 'true') return;

        const fill = this.querySelector('.shipping-threshold__fill');
        if (!fill) return;

        // Separate keys for the drawer and the cart page (both live in the DOM).
        const key = this.closest('cart-drawer') ? 'drawer' : 'main';
        const target = fill.style.width || '0%';
        const prev = lastWidth.has(key) ? lastWidth.get(key) : target;
        lastWidth.set(key, target);

        if (prev === target) return;

        // Paint the previous width instantly, then transition to the target.
        fill.style.transition = 'none';
        fill.style.width = prev;
        void fill.offsetWidth; // force reflow so `prev` is committed
        requestAnimationFrame(() => {
          fill.style.transition = '';
          fill.style.width = target;
        });
      }
    }
  );
}
